// Australian declared allergens (FSANZ Standard 1.2.3 - Plain English Allergen Labelling).
export const ALLERGENS = [
  { id: "gluten", label: "Gluten (wheat/rye/barley/oats)" },
  { id: "crustacea", label: "Crustacea" },
  { id: "egg", label: "Egg" },
  { id: "fish", label: "Fish" },
  { id: "milk", label: "Milk" },
  { id: "peanut", label: "Peanut" },
  { id: "sesame", label: "Sesame" },
  { id: "soy", label: "Soybean" },
  { id: "tree_nut", label: "Tree nuts" },
  { id: "lupin", label: "Lupin" },
  { id: "mollusc", label: "Mollusc" },
  { id: "sulphites", label: "Added sulphites ≥10mg/kg" },
];

export const allergenLabel = (id) => ALLERGENS.find((a) => a.id === id)?.label || id;

// PEAL plain-English names for the compliance declaration ("Contains: …").
const PEAL_NAMES = {
  gluten: "Gluten", crustacea: "Crustacea", egg: "Egg", fish: "Fish",
  milk: "Milk", peanut: "Peanut", sesame: "Sesame", soy: "Soy",
  tree_nut: "Tree nuts", lupin: "Lupin", mollusc: "Mollusc", sulphites: "Sulphites",
};
export const declarationName = (id) =>
  PEAL_NAMES[id] || String(id || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Trigger words used to auto-detect allergens inside a free-text ingredient
// statement — powers both the "may contain" warning and the printed
// bold-allergen emphasis (AU PEAL / FSANZ 1.2.4). Mirror of the backend
// _ALLERGEN_TRIGGERS map in tspl_printer.py.
export const ALLERGEN_TRIGGERS = {
  gluten: ["gluten", "wheat", "barley", "rye", "oats", "oat", "spelt", "malt", "semolina", "couscous", "farro", "bulgur"],
  crustacea: ["crustacea", "prawn", "prawns", "shrimp", "shrimps", "crab", "lobster", "crayfish"],
  egg: ["egg", "eggs", "albumen", "mayonnaise"],
  fish: ["fish", "anchovy", "anchovies", "salmon", "tuna", "cod", "sardine", "sardines", "mackerel"],
  milk: ["milk", "dairy", "butter", "cheese", "cream", "whey", "casein", "lactose", "yoghurt", "yogurt", "ghee"],
  peanut: ["peanut", "peanuts", "groundnut", "groundnuts"],
  sesame: ["sesame", "tahini"],
  soy: ["soy", "soya", "soybean", "soybeans", "edamame", "tofu"],
  tree_nut: ["almond", "almonds", "hazelnut", "hazelnuts", "cashew", "cashews", "walnut", "walnuts", "pecan", "pecans", "pistachio", "pistachios", "macadamia", "macadamias", "brazil"],
  lupin: ["lupin", "lupine"],
  mollusc: ["mollusc", "molluscs", "oyster", "oysters", "mussel", "mussels", "squid", "octopus", "scallop", "scallops", "clam", "clams"],
  sulphites: ["sulphite", "sulphites", "sulfite", "sulfites", "sulphur dioxide"],
};

const _escape = (w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Scan ingredient text and return the allergen ids detected (whole-word,
// case-insensitive).
export function detectAllergens(text) {
  const low = String(text || "").toLowerCase();
  if (!low.trim()) return [];
  const found = [];
  for (const [id, words] of Object.entries(ALLERGEN_TRIGGERS)) {
    if (words.some((w) => new RegExp(`\\b${_escape(w)}\\b`, "i").test(low))) found.push(id);
  }
  return found;
}

// Split ingredient text into runs, flagging runs that match a DECLARED
// allergen's trigger word so the on-screen preview can bold them exactly
// like the printed TSPL sticker does.
export function splitAllergenSegments(text, declaredAllergens = []) {
  const src = String(text || "");
  if (!src) return [{ text: "", bold: false }];
  const words = new Set();
  for (const a of declaredAllergens || []) {
    const key = String(a).trim().toLowerCase().replace(/ /g, "_");
    for (const w of ALLERGEN_TRIGGERS[key] || []) words.add(w);
  }
  if (!words.size) return [{ text: src, bold: false }];
  const pattern = new RegExp(`\\b(${[...words].map(_escape).join("|")})\\b`, "gi");
  const segs = [];
  let last = 0;
  let m = pattern.exec(src);
  while (m !== null) {
    if (m.index > last) segs.push({ text: src.slice(last, m.index), bold: false });
    segs.push({ text: m[0], bold: true });
    last = m.index + m[0].length;
    if (m.index === pattern.lastIndex) pattern.lastIndex += 1;
    m = pattern.exec(src);
  }
  if (last < src.length) segs.push({ text: src.slice(last), bold: false });
  return segs;
}

// Common ingredient-template groupings so long lists stay findable.
export const TEMPLATE_CATEGORIES = [
  "General", "Sauces", "Doughs & Bases", "Dressings", "Marinades",
  "Spice Mixes", "Fillings", "Sides", "Desserts", "Drinks",
];
